// FE-PINV-072 — bulk-action REST client + React Query hook.
//
// Only ``move-stage`` rides the new transactional bulk endpoint
// introduced in this commit. The other five bulk actions (assign
// producer, apply playbook, add to list, bulk log touchpoint, archive)
// fan out sequentially through pre-existing per-row endpoints — the
// fan-out lives in the action handlers, not here.

import {
  useMutation, useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { api } from "@/api/client";

import { RENEWAL_BOARD_QUERY_KEY } from "../RenewalBoard/hooks";


export interface BulkMoveStagePayload {
  opportunity_ids: string[];
  target_stage: string;
  confirm_reopen?: boolean;
  lost_reason?: string;
  lost_to_broker?: string;
  lost_notes?: string;
}


export interface BulkMoveStageSuccess {
  updated: string[];
}


export interface BulkMoveStageError {
  detail: string;
  errors?: { opportunity_id: string; detail: string }[];
}


export function bulkMoveStage(
  payload: BulkMoveStagePayload,
): Promise<BulkMoveStageSuccess> {
  return api.post<BulkMoveStageSuccess>(
    "/api/v1/sales/opportunities/bulk/move-stage",
    payload,
  );
}


export function useBulkMoveStageMutation(): UseMutationResult<
  BulkMoveStageSuccess, Error, BulkMoveStagePayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: bulkMoveStage,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [RENEWAL_BOARD_QUERY_KEY] });
    },
  });
}


// ── Per-row fan-out helpers ─────────────────────────────────────────


export function patchOpportunityProducer(
  opportunity_id: string, producer_id: string | null,
): Promise<unknown> {
  return api.patch(
    `/api/v1/sales/opportunities/${opportunity_id}`,
    { producer_id },
  );
}


export function archiveOpportunity(
  opportunity_id: string,
): Promise<unknown> {
  return api.delete(`/api/v1/sales/opportunities/${opportunity_id}`);
}
